import * as React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { CircleDollarIcon, UserGroupIcon, WalletIcon } from '@revfluence/fresh-icons/solid/esm';
import { TOfferStats } from './types';
import { StatCard } from './StatCard';

interface StatsCardsProps {
  data: TOfferStats;
  queries?: any;
  isMembersLoading?: boolean;
  memberConversionCount?: number | null;
  membersWithPayoutDue?: number | null;
}

export const StatsCards: React.FC<StatsCardsProps> = ({
  data,
  queries,
  isMembersLoading = false,
  memberConversionCount,
  membersWithPayoutDue
}) => {
  // Use member-dependent stats from props if available, otherwise fall back to data
  const finalMemberConversionCount = memberConversionCount ?? data.memberConversionCount;
  const finalMembersWithPayoutDue = membersWithPayoutDue ?? data.membersWithPayoutDue;

  // Determine if member stats are loading
  const isMemberStatsLoading = isMembersLoading || finalMemberConversionCount === null || finalMembersWithPayoutDue === null;

  return (
    <div className="grid grid-cols-1 gap-4 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      <StatCard
        title="Members"
        bgColor="bg-purple-5"
        widgetIcon={<UserGroupIcon className="text-grey-0" fontSize={20} />}
        value={queries?.offerMembers?.length || 0}
        metricType="count"
        subHeading={isMemberStatsLoading ? 'Loading...' : `${finalMemberConversionCount} with Conversions`}
        tooltip="The number of members added across this offer. The metric below shows how many of them have driven at least one conversion."
        isLoading={isMemberStatsLoading}
      />
      <StatCard
        title="Conversions"
        bgColor="bg-blue-5"
        widgetIcon={<FontAwesomeIcon icon={faGlobe} className="text-white" fontSize={20} />}
        value={data.conversions}
        metricType="number"
        subHeading={`${data.clicks || 0} clicks`}
        primaryPopoverText={data.conversions?.toLocaleString('en-US')}
        tooltip="Shows the number of conversions driven by members across this offer. The metric below shows the number of clicks."
      />
      <StatCard
        title="Total Sales"
        bgColor="bg-teal-5"
        widgetIcon={<CircleDollarIcon className="text-white" fontSize={20} />}
        value={data.sales}
        metricType="currency"
        subHeading={`$${(finalMemberConversionCount ? data.sales / (finalMemberConversionCount || 1) : 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Sales per Member`}
        primaryPopoverText={`$${data.sales?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        tooltip="Shows the total sales driven by members across this offer. The metric below shows the average sales per member."
        secondaryValue={queries?.offerStats?.currencies?.[0] !== 'USD' ? { value: queries?.offerStats?.salesBase, symbol: queries?.offerStats?.currencies?.[0] } : undefined}
        isLoading={isMemberStatsLoading}

      />
      <StatCard
        title="Commissions Earned"
        bgColor="bg-lime-5"
        widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
        value={data.payoutEarned}
        metricType="currency"
        subHeading={`$${queries?.offerMembers?.length > 0 ? (data.payoutEarned / (finalMemberConversionCount || 1)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'} Avg per Member`}
        primaryPopoverText={`$${data.payoutEarned?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        tooltip="Shows the total commissions earned by members across this offer. The metric below shows the average commission earned per member."
        secondaryValue={queries?.offerStats?.currencies?.[0] !== 'USD' ? { value: queries?.offerStats?.payoutEarnedBase, symbol: queries?.offerStats?.currencies?.[0] } : undefined}
        isLoading={isMemberStatsLoading}
      />
      <StatCard
        title="Payments Due"
        bgColor="bg-yellow-5"
        widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
        value={Number(((data.payoutEarned || 0) - (data.payoutMade || 0)).toFixed(2))}
        metricType="currency"
        subHeading={isMemberStatsLoading ? 'Loading...' : `${finalMembersWithPayoutDue} Members`}
        primaryPopoverText={`$${data.payoutEarned - data.payoutMade} Payments Due`}
        tooltip="Shows the total unpaid commissions for members across this offer. The metric below shows the number of members with pending payments."
        isLoading={isMemberStatsLoading}
      />
    </div>
  );
};
